import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Fabric, fabricClasses, fabricClassValues, 
  fabricClassValueToLabel, fabricTypeValueToLabel
} from '../models/Fabric';
import { getFabrics, deleteFabric } from '../services/fabricService';
import { toast } from 'react-toastify';
import { API_URL } from '../config/constants';

const FabricPool: React.FC = () => {
  const [fabrics, setFabrics] = useState<Fabric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterType, setFilterType] = useState('');

  // Kumaşları API'den yükleme
  useEffect(() => {
    const loadFabrics = async () => {
      try {
        setLoading(true);
        const data = await getFabrics();
        setFabrics(data);
        setError(null);
      } catch (err) {
        console.error('Kumaşlar yüklenirken hata oluştu:', err);
        setError('Kumaşlar yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        toast.error('Kumaşlar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadFabrics();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu kumaşı silmek istediğinize emin misiniz?')) {
      try {
        await deleteFabric(id);
        setFabrics(fabrics.filter(fabric => fabric._id !== id));
        toast.success('Kumaş başarıyla silindi');
      } catch (err) {
        console.error('Kumaş silinirken hata oluştu:', err);
        toast.error('Kumaş silinirken bir hata oluştu');
      }
    }
  };

  // Filtreleme ve içerik gösterme için helper functions
  const getLocalizedFabricClass = (value: string): string => {
    return fabricClassValueToLabel(value);
  };

  const getLocalizedFabricType = (value: string): string => {
    return fabricTypeValueToLabel(value);
  };

  // Filtreleme için özel işleme
  const filteredFabrics = fabrics.filter(fabric => {
    const matchesSearch = 
      fabric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (fabric.composition && fabric.composition.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (fabric.notes && fabric.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (fabric.supplier && fabric.supplier.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (fabric.description && fabric.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
    // Filtrelemeyi İngilizce değerlerle eşleştir
    const matchesClass = filterClass ? 
      fabric.fabricClass === fabricClassValues[fabricClasses.indexOf(filterClass)] : true;
    const matchesType = filterType ? fabric.type === filterType : true;
    
    return matchesSearch && matchesClass && matchesType;
  });

  // Kumaş tiplerini dinamik olarak çıkarma
  const fabricTypeOptions = Array.from(new Set(fabrics.map(f => f.type)));

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Kumaş Havuzu</h1>
        <Link 
          to="/fabrics/create"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm"
        >
          Yeni Kumaş Ekle
        </Link>
      </div>

      {/* Filtreleme ve arama */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="flex-1">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Ara</label>
          <input
            type="text"
            id="search"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Kumaş adı, içerik veya not..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="class-filter" className="block text-sm font-medium text-gray-700 mb-1">Kumaş Sınıfı</label>
          <select
            id="class-filter"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
          >
            <option value="">Tüm Sınıflar</option>
            {fabricClasses.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 mb-1">Kumaş Tipi</label>
          <select
            id="type-filter"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Tüm Tipler</option>
            {fabricTypeOptions.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Kumaş kartları grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFabrics.map(fabric => (
          <div key={fabric._id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="h-48 w-full overflow-hidden">
              {fabric.imageUrl ? (
                <img 
                  src={`${API_URL}${fabric.imageUrl.startsWith('/') ? '' : '/'}${fabric.imageUrl}`}
                  alt={fabric.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Görsel yüklenemezse yedek görüntü göster
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/placeholder-fabric.png'; // Yerel bir placeholder görseli
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                  Görsel Yok
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-lg">{fabric.name}</h3>
                <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                  {getLocalizedFabricClass(fabric.fabricClass)}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {fabric.composition} | {fabric.weight} gr/m² | {fabric.width} cm
              </p>
              <div className="flex flex-wrap gap-1 mb-3">
                {fabric.colors && fabric.colors.length > 0 && fabric.colors.slice(0, 3).map(color => (
                  <span 
                    key={color} 
                    className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
                  >
                    {color}
                  </span>
                ))}
                {fabric.properties && fabric.properties.slice(0, 2).map(prop => (
                  <span 
                    key={prop} 
                    className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full"
                  >
                    {prop}
                  </span>
                ))}
                {(fabric.colors?.length > 3 || fabric.properties?.length > 2) && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                    +{(fabric.colors?.length || 0) - 3 + (fabric.properties?.length || 0) - 2}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center mt-3">
                <div className="text-sm text-gray-600">
                  {fabric.supplier || '-'}
                </div>
                <div className="flex space-x-2">
                  <Link 
                    to={`/fabrics/${fabric._id}`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Detay
                  </Link>
                  <Link 
                    to={`/fabrics/${fabric._id}/edit`}
                    className="text-green-600 hover:text-green-800"
                  >
                    Düzenle
                  </Link>
                  <button 
                    onClick={() => handleDelete(fabric._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Eğer kumaş yoksa */}
      {filteredFabrics.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            {fabrics.length === 0 
              ? 'Henüz hiç kumaş eklemesi yapılmamış.'
              : 'Aradığınız kriterlere uygun kumaş bulunamadı.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default FabricPool;
